import React, { useContext, useState, useEffect, useCallback, useRef } from "react";
import { Dropdown, List, Typography, Empty, Grid } from "antd";
import "../../assets/css/NotificationPanel.css";

import useHttp from "../../hooks/useHttp";
import { AppContext } from "../../context/AppContext";
import { successNotification } from "../../pages/utils/CommonNotifications";
import { usePusher } from "../../context/PusherContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotificationsStore } from "../../stores/notificationsStore";
import HTag from "../custom/tagComponents/HTag";

const { useBreakpoint } = Grid;
const { Text } = Typography;

const NotificationPanel = ({ chatView = false }) => {
  const { fetchData, fetchDataByPagination } = useHttp();
  const [appProperties] = useContext(AppContext);
  const userId = appProperties?.currentUser?.osyncUserId;
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;


  const isNotificationUnread = useCallback((notification, currentUserId) => {
    if (!notification?.recipients || !currentUserId) return true;
    const recipient = notification.recipients.find(r => r.osyncUserId === currentUserId);
    return recipient?.status === 0; 
  }, []);

  // Helper function to extract contactInfo from associatedModuleDetails
  const getContactInfo = useCallback((notification) => {
    if (notification?.associatedModuleDetails) {
      const details = notification.associatedModuleDetails;
      return details.contactInfo ?? details["contactInfo"]
        ?? details.conversationId ?? details["conversationId"]
        ?? (typeof details === "string" ? details : JSON.stringify(details));
    }
    return null;
  }, []);

  const [isOpen, setIsOpen] = useState(false);
  const isOpenRef = useRef(isOpen);
  const listRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const hasInitiallyLoadedRef = useRef(false);

  const notifications = useNotificationsStore((s) => s.notifications);
  const unreadCount = useNotificationsStore((s) => s.unreadCount);
  const loading = useNotificationsStore((s) => s.loading);

  const fetchUnreadCount = useNotificationsStore((s) => s.fetchUnreadCount);
  const fetchNotifications = useNotificationsStore((s) => s.fetchNotifications);
  const markAsRead = useNotificationsStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationsStore((s) => s.markAllAsRead);
  const onIncomingNotification = useNotificationsStore((s) => s.onIncomingNotification);
  const markNotificationsAsReadByIds = useNotificationsStore((s) => s.markNotificationsAsReadByIds);

  useEffect(() => {
    if (userId) {
      fetchUnreadCount({ fetchData, appProperties, userId });
    }
  }, [userId]);

  const { connect, subscribe, unSubscribe } = usePusher();

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);


  useEffect(() => {
    if (!userId) return;

    let isMounted = true;

    const setupNotificationSubscription = async () => {
      try {
        const connectionSuccess = await connect(false, true);
        if (!connectionSuccess || !isMounted) return;

        const handleNewNotification = (data) => {
          if (!data) return;

          try {
            let payload = data;

            if (typeof payload === "string") {
              try {
                payload = JSON.parse(payload);
              } catch (e) {
                console.warn("Failed to parse notification string payload:", e);
                return;
              }
            }

            // Handle nested data field
            if (payload?.data) {
              if (typeof payload.data === "string") {
                try {
                  payload = JSON.parse(payload.data);
                } catch (e) {
                  console.warn("Failed to parse nested notification payload:", e);
                  return;
                }
              } else if (typeof payload.data === "object") {
                payload = payload.data;
              }
            }

            // Handle bulk-read payload
            if (payload && Array.isArray(payload.notificationIds) && 
                (payload.notificationId === undefined || payload.notificationId === null)) {
              if (payload.notificationIds.length > 0 && userId) {
                markNotificationsAsReadByIds({ notificationIds: payload.notificationIds, userId });
              }
              return;
            }

            const notificationsList = Array.isArray(payload)
              ? payload
              : [payload];

            if (notificationsList.length === 0) return;

            for (const notification of notificationsList) {
              if (!notification?.notificationId) continue;
              

              const currentNotifications = useNotificationsStore.getState().notifications || [];
              const existingNotification = currentNotifications.find(
                n => n.notificationId === notification.notificationId
              );
              const isNewNotification = !existingNotification;

              onIncomingNotification(notification, {
                addToList: true,
                incrementUnreadCount: isNewNotification,
              });
            }
          } catch (e) {
            console.error("Error handling notification event:", e);
          }
        };

        subscribe("NOTIFICATION_UPDATE", handleNewNotification, false, true);
      } catch (error) {
        console.error("Error setting up notification subscriptions:", error);
      }
    };

    setupNotificationSubscription();

    return () => {
      isMounted = false;
      unSubscribe("NOTIFICATION_UPDATE");
    };
  }, [userId, connect, subscribe, unSubscribe, onIncomingNotification, markNotificationsAsReadByIds]);


  useEffect(() => {
    if (!userId || !isOpen) return;
    
    // If we have no notifications, fetch from API
    if (notifications.length === 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
      fetchNotifications({ fetchDataByPagination, appProperties, userId, reset: true });
    } else if (notifications.length > 0 && !hasInitiallyLoadedRef.current) {
      hasInitiallyLoadedRef.current = true;
    }
  }, [isOpen, userId, fetchNotifications, fetchDataByPagination, appProperties, notifications.length]);

  const handleNotificationClick = useCallback(
    (notification) => {
      if (!notification) return;

      setIsOpen(false);

      if (chatView) {
        const deepLink = constructDeepLink(notification);
        if (deepLink) {
          window.open(deepLink, '_blank', 'noopener,noreferrer');
      
          markAsRead({ fetchData, appProperties, notification });
          return;
        }
      }

      const contactId = getContactInfo(notification);


      if (notification?.type === 3) {
        navigate(`/voice/history${location.search}`);
      } else if (contactId) {
        navigate(`/inbox/${contactId}${location.search}`);
      }

      // Mark as read - optimistic update handles UI immediately
      markAsRead({ fetchData, appProperties, notification });
    },
    [chatView, , navigate, markAsRead, fetchData, appProperties, location.search, getContactInfo]
  );

  const handleMarkAllAsRead = () => {
    if (!userId) return;

    markAllAsRead({ fetchData, appProperties, userId })
      .then(() => {
        successNotification("All notifications marked as read");
      })
  };

  const getNotificationType = (typeNumber) => {
    switch (typeNumber) {
      case 1:
        return "ASSIGNED";
      case 2:
        return "MESSAGE";
      case 3:
        return "MISSED_CALL";
      default:
        return "NOTIFICATION";
    }
  };

  const getNotificationIcon = (typeNumber) => {
    const type = getNotificationType(typeNumber);

    switch (type) {
      case "ASSIGNED":
        return <div className="actionIconsSprite-2 assignedNotificationIcon" />;
      case "MESSAGE":
        return <div className="actionIconsSprite-2 messageNotificationIcon" />;
      case "MISSED_CALL":
        return <div className="actionIconsSprite-2 missedCallNotificationIcon" />;
      default:
        return <div className="actionIconsSprite-2 messageNotificationIcon" />;
    }
  };

  const formatTimestamp = (isoString) => {
    if (!isoString) return "";
  
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
  
    const now = new Date();
    const diffMs = now - date;
    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(diffMs / 3600000);
    const remainingMinutes = minutes % 60;
  
    // Normalize dates to local midnight for accurate comparison
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  
    const startOfNotificationDay = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
  
    const isToday = startOfNotificationDay.getTime() === startOfToday.getTime();
    const isYesterday =
      startOfNotificationDay.getTime() === startOfYesterday.getTime();
  
    const formatTime = (d) => {
      let h = d.getHours();
      const m = d.getMinutes();
      const ampm = h >= 12 ? "PM" : "AM";
      h = h % 12 || 12;
      return `${h}:${m.toString().padStart(2, "0")} ${ampm}`;
    };
  
    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes} min${minutes > 1 ? "s" : ""} ago`;
  
    if (isToday) return `Today, ${formatTime(date)}`;
    if (isYesterday) return `Yesterday, ${formatTime(date)}`;
  
    if (hours < 24) {
      return remainingMinutes > 0
        ? `${hours} hr${hours > 1 ? "s" : ""} ${remainingMinutes} min ago`
        : `${hours} hr${hours > 1 ? "s" : ""} ago`;
    }
  
    // Older dates
    return date.toLocaleDateString();
  };

  const parseNotificationContent = (notification) => {
    if (!notification) return { title: "", subtitle: "" };

    const { notificationContent } = notification;

    if (!notificationContent) {
      return { title: "Notification", subtitle: "" };
    }

    try {
      const parsed = JSON.parse(notificationContent);
      if (parsed && typeof parsed === "object") {
        const title = parsed.title;
        const body = parsed.body;
        return {
          title: title,
          subtitle: body,
        };
      }
    } catch (e) {
      console.error("Error parsing notification content:", e);
    }

    // Fallback for missed calls
    if (notification?.type === 3) {
      return {
        title: notificationContent,
        subtitle: "View the call log and reconnect with the customer.",
      };
    }

    return {
      title: notificationContent,
      subtitle: "",
    };
  };

  const constructDeepLink = useCallback((notification) => {
    if (!notification) return null;
    if (notification?.type === 3) {
      return `/voice/history${location.search}`;
    }
    const conversationOrContactId = notification?.associatedModuleDetails?.conversationId
      ?? notification?.associatedModuleDetails?.contactId;
    return `/inbox/${conversationOrContactId ?? ""}${location.search}`;
  }, [location.search]);

  // Load more when sentinel at bottom becomes visible (viewport root works regardless of which element scrolls)
  useEffect(() => {
    if (!isOpen || !userId || notifications.length === 0) return;

    const sentinelEl = loadMoreSentinelRef.current;
    if (!sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        const state = useNotificationsStore.getState();
        if (state.cursor === null || state.loading) return;
        fetchNotifications({ fetchDataByPagination, appProperties, userId, reset: false });
      },
      {
        root: null,
        rootMargin: "0px 0px 80px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [isOpen, userId, notifications.length, fetchNotifications, fetchDataByPagination, appProperties]);

  const notificationContent = (
    <div className="notification-panel-dropdown d-flex flex-column bg-white rounded-3 overflow-hidden border shadow">
      <div className="d-flex justify-content-between align-items-center notification-header border-bottom bg-white p-3">
        <Typography.Text strong className="hs-fs-14 hs-fw-500">
          Notifications
        </Typography.Text>
        <div className="d-flex align-items-center gap-2">
          {unreadCount > 0 && (
            <span
              onClick={handleMarkAllAsRead}
              className="mark-read text-decoration-underline cursor-pointer"
            >
              Mark all as read
            </span>
          )}
          {isMobile && (
            <Typography.Text
              onClick={() => setIsOpen(false)}
              className="notification-close-btn d-inline-flex align-items-center justify-content-center rounded cursor-pointer ms-2 hs-fs-16 hs-fw-600"
            >
              ✕
            </Typography.Text>
          )}
        </div>
      </div>

      {loading && notifications.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center notification-loading-container py-4">
          <div className="actionIconsSprite dropDownLoading" />
        </div>
      ) : notifications.length === 0 ? (
        <Empty description="No notifications" className="p-5" />
      ) : (
        <div ref={listRef} className="notification-list flex-grow-1 overflow-auto p-0 min-h-0 hs-default-list-scroll">
          <List
            dataSource={notifications}
            rowKey="notificationId"
            renderItem={(item) => {
              const isUnread = isNotificationUnread(item, userId);
              const { title, subtitle } = parseNotificationContent(item);

              return (
                <List.Item
                  onClick={() => handleNotificationClick(item)}
                  className={`d-flex align-items-start flex-nowrap notification-item gap-3 cursor-pointer border-bottom ${isUnread ? "hs-bg-light-gray" : ""}`}
                >
                  <div className="p-1 flex-shrink-0">
                    {getNotificationIcon(item.type)}
                  </div>
                  <div className="d-flex flex-column flex-grow-1 notification-text gap-1 min-w-0 overflow-hidden">
                    <div className="d-flex align-items-center justify-content-between min-w-0 gap-1">
                      <span className="hs-fs-12 hs-fw-500 hs-color-BlackRussian text-truncate">
                        {title}
                      </span>
                      {isUnread && (
                        <div className="notification-unread-indicator rounded-circle flex-shrink-0" />
                      )}
                    </div>
                    {subtitle && (
                      <span className="hs-fs-11 hs-fw-400 hs-color-dark-grey-2 text-truncate d-block">
                        {subtitle}
                      </span>
                    )}
                    <Text className="hs-fs-11 hs-fw-400 hs-color-md-grey" data-cy="notification-timestamp">
                      {formatTimestamp(item.createdAt)}
                    </Text>
                  </div>
                </List.Item>
              );
            }}
          />
          <div ref={loadMoreSentinelRef} className="notification-load-more-sentinel" aria-hidden="true" />
          {loading && notifications.length > 0 && (
            <div className="d-flex align-items-center justify-content-center" data-cy="notification-list-loading">
              <div className="actionIconsSprite dropDownLoading position-absolute" />
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Dropdown 
      overlay={notificationContent} 
      trigger={["click"]} 
      placement={isMobile ? "bottom" : "bottomRight"} 
      open={isOpen} 
      onOpenChange={setIsOpen}
      overlayClassName={isMobile ? "notification-dropdown-mobile" : ""}
    >
      <div className="position-relative cursor-pointer">
        <div className="actionIconsSprite-2 notificationIcon" aria-label="Notifications" role="button" />
        {unreadCount > 0 && (
          <div className="d-flex align-items-center justify-content-center notification-count-badge">
            <HTag count={unreadCount} maxDisplay={99} className="notification-count-badge-tag" />
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default NotificationPanel;